import { useAuth0 } from '@auth0/auth0-react';
import { NewBuildNotice, usePresenceHeartbeat, useHostAuth } from './shared/ui';
import { lazy, Suspense, type ReactNode } from 'react';
import { Route, Routes } from 'react-router-dom';
import { MenuBar } from './layout/menu-bar';
import { environment } from '../environments/environment';
import { DashboardPage } from './features/dashboard/pages/dashboard-page';
// All three of these live in one 1.1KB module, and the callback in it is on
// the sign-in path, so there is nothing to win by splitting the file.
import { CallbackPage, NotFoundPage, UnauthorizedPage } from './features/misc/pages';

/**
 * Everything but the two screens that must not wait for a download.
 *
 * The dashboard is what `/` lands on, so splitting it would only add a round
 * trip to the one navigation everybody makes; the callback is the step Auth0
 * returns to, and delaying that delays signing in. Both are small. The rest
 * are screens most people never open, so they are fetched when asked for.
 *
 * The account screens come from `@iqd/shared-app/screens`, not the barrel:
 * the barrel is reached statically from a dozen files here, so importing them
 * through it would leave them in the entry chunk whatever `lazy` says.
 */
/**
 * The quotation screen sits outside the quote routes on purpose: it needs the
 * option uuid from the URL and nothing else, so it costs none of the drawing
 * engine those routes build.
 */
const QuotationPage = lazy(() =>
  import('./features/quotation/quotation-page').then((module) => ({
    default: module.QuotationPage,
  })),
);
const NotificationsPage = lazy(() =>
  import('./features/notifications/notifications-page').then((module) => ({
    default: module.NotificationsPage,
  })),
);
const TermsOfUsePage = lazy(() =>
  import('@iqd/shared-screens').then((module) => ({ default: module.TermsOfUsePage })),
);
const SupportPage = lazy(() =>
  import('@iqd/shared-screens').then((module) => ({ default: module.SupportPage })),
);
const SupportCompletePage = lazy(() =>
  import('@iqd/shared-screens').then((module) => ({ default: module.SupportCompletePage })),
);
const UserSettingsPage = lazy(() =>
  import('@iqd/shared-screens').then((module) => ({ default: module.UserSettingsPage })),
);
const CompanySettingsPage = lazy(() =>
  import('@iqd/shared-screens').then((module) => ({ default: module.CompanySettingsPage })),
);

/**
 * The quote section carries the drawing engine — pixi, and the 3D renderer
 * behind a dynamic import of its own — so the whole subtree is split out.
 * Someone who signs in to check a price never downloads it.
 */
const importQuoteRoutes = () => import('./features/quote/quote-routes');

const QuoteRoutes = lazy(() =>
  importQuoteRoutes().then((module) => ({ default: module.QuoteRoutes })),
);

/**
 * Starts the quote chunk before it is asked for.
 *
 * It is the heaviest thing here by a distance — pixi and the drawing engine —
 * and it is also the one screen people come to the portal for, so the click on
 * Quote is the one worth having nothing to wait for. Pointing at the menu item
 * is enough warning. Failure is ignored: this is only a head start, and the
 * `lazy` above still reports through the boundary if the real load fails.
 */
function prefetchQuoteRoutes() {
  importQuoteRoutes().catch(() => undefined);
}

/** Sends anyone not signed in to Auth0 rather than showing them an empty screen. */
function RequireAuth({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useHostAuth();
  const { loginWithRedirect } = useAuth0();

  if (!isAuthenticated) {
    void loginWithRedirect({ appState: { returnTo: window.location.pathname } });
    return null;
  }
  return <>{children}</>;
}

export function App() {
  // The portal has no shell component, so this is its "every screen" — the
  // same job `AppShell` does in iQD Pro. Reported under its own app key: a
  // person with both products open is two entries, which is the truth.
  usePresenceHeartbeat('distributor-portal');

  return (
    <div className="min-h-screen bg-white text-hilevel-slate">
      <MenuBar onPrefetchQuote={prefetchQuoteRoutes} />
      {/* A deploy that has landed under this tab is news about the whole page,
          so it sits with the chrome rather than inside any one screen. */}
      <NewBuildNotice />
      <main className="pb-8">
        <Suspense fallback={<p className="m-8 text-center text-sm text-hilevel-gray">Loading…</p>}>
          <Routes>
            <Route path="/" element={<DashboardPage />} />

            {/* The quote flow shares one engine: a live session and one pixi
                renderer, built once for the whole flow rather than per screen. */}
            <Route
              path="/quote/*"
              element={
                <RequireAuth>
                  <QuoteRoutes />
                </RequireAuth>
              }
            />

            <Route
              path="/quotation/:optionUuid"
              element={
                <RequireAuth>
                  <QuotationPage />
                </RequireAuth>
              }
            />

            <Route path="/terms-of-use" element={<TermsOfUsePage />} />

            <Route
              path="/user-settings"
              element={
                <RequireAuth>
                  <UserSettingsPage
                    auth0Domain={environment.auth0.domain}
                    auth0ClientId={environment.auth0.clientId}
                  />
                </RequireAuth>
              }
            />
            <Route
              path="/company-settings"
              element={
                <RequireAuth>
                  <CompanySettingsPage apiUrl={environment.apiUrl} />
                </RequireAuth>
              }
            />
            <Route
              path="/notifications"
              element={
                <RequireAuth>
                  <NotificationsPage />
                </RequireAuth>
              }
            />

            {/* Support is reachable signed out: somebody locked out of their
                account is exactly who needs it. */}
            <Route path="/support" element={<SupportPage />} />
            <Route path="/support/complete" element={<SupportCompletePage />} />

            <Route path="/unauthorized" element={<UnauthorizedPage />} />
            <Route path="/callback" element={<CallbackPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </main>
    </div>
  );
}
